import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as providerService from "@/shared/api/services/providerService";
import { motion } from "framer-motion";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent } from "@/shared/components/ui/card";
import {
  Search,
  Server,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Wrench,
  Eye,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName } from "@/shared/utils";

const statusConfig = {
  active: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  inactive: { icon: XCircle, color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  maintenance: { icon: Wrench, color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  degraded: { icon: AlertTriangle, color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" },
};

const typeColors = {
  bank: "bg-blue-100 text-blue-700",
  ewallet: "bg-purple-100 text-purple-700",
  qr: "bg-emerald-100 text-emerald-700",
  card: "bg-amber-100 text-amber-700",
};

export default function ResellerProviders() {
  const [search, setSearch] = useState("");
  const resellerName = "Asia Gaming Network";
  const { language, t } = useLanguage();

  // Helper function to translate provider status
  const translateStatus = (status) => {
    const statusMap = {
      "active": t("active"),
      "inactive": t("inactive"),
      "degraded": t("degraded"),
      "maintenance": t("maintenance"),
    };
    return statusMap[status] || status;
  };

  // Helper function to translate provider type
  const translateType = (type) => {
    const typeMap = {
      "bank": t("bank"),
      "ewallet": t("ewallet"),
      "qr": t("qr"),
      "card": t("card"),
    };
    return typeMap[type] || type;
  };

  const { data: providers = [], isLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: () => providerService.getAllProviders(),
  });

  const filteredProviders = providers.filter(p => {
    const providerName = getProviderName(p, language);
    return providerName?.toLowerCase().includes(search.toLowerCase()) ||
      p.code?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <ResellerLayout currentPage="ResellerProviders" resellerName={resellerName}>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">{t("providers")}</h1>
            <Badge variant="outline" className="gap-1">
              <Eye className="h-3 w-3" />
              {t("viewOnly")}
            </Badge>
          </div>
          <p className="text-slate-500">{t("paymentProviderStatus")}</p>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder={t("searchProviders")} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-slate-500">{t("loading")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProviders.map((provider, i) => {
              const status = statusConfig[provider.status] || statusConfig.inactive;
              const StatusIcon = status.icon;
              return (
                <motion.div
                  key={provider.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                            <Server className="h-6 w-6 text-violet-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{getProviderName(provider, language)}</h3>
                            <p className="text-sm text-slate-500">{provider.code}</p>
                          </div>
                        </div>
                        <div className={`w-3 h-3 rounded-full ${status.dot}`} />
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge className={typeColors[provider.type] || typeColors.bank}>
                          {translateType(provider.type)}
                        </Badge>
                        <Badge className={`${status.color} gap-1`}>
                          <StatusIcon className="h-3 w-3" />
                          {translateStatus(provider.status)}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">{t("successRate")}</p>
                          <p className="font-semibold">{provider.success_rate || 100}%</p>
                        </div>
                        <div>
                          <p className="text-slate-500">{t("response")}</p>
                          <p className="font-semibold">{provider.avg_response_time || 0}ms</p>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2">
                        {provider.supports_deposit && (
                          <Badge variant="outline" className="text-xs">{t("deposit")}</Badge>
                        )}
                        {provider.supports_withdrawal && (
                          <Badge variant="outline" className="text-xs">{t("withdrawal")}</Badge>
                        )}
                        {provider.supports_qr && (
                          <Badge variant="outline" className="text-xs">{t("qr")}</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

      </div>
    </ResellerLayout>
  );
}